import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

export interface ExpoStockEntryDto {
  id: string;
  greenhouseStockEntryId: string;
  cropTypeId: string;
  cropTypeName: string;
  variety?: string | null;
  pricingMode: string;
  stockUnit: string;
  trayType?: string | null;
  totalMovedQuantity: number;
  soldQuantity: number;
  returnedQuantity: number;
  availableQuantity: number;
  isActive: boolean;
  firstMovedAt: string;
  lastMovementAt?: string | null;
  createdAt: string;
}

export interface ExpoStockMovementRequestDto {
  id: string;
  movementType: "MoveToExpo" | "ReturnToGreenhouse" | string;
  greenhouseStockEntryId: string;
  expoStockEntryId?: string | null;
  requestedQuantity: number;
  includeFillingReserve: boolean;
  quantityFromFillingReserve: number;
  reason: string;
  status: string;
  requestedByUserId: string;
  requestedByUserName: string;
  requestedAt: string;
  decisionByUserId?: string | null;
  decisionByUserName?: string | null;
  decisionAt?: string | null;
  decisionComment?: string | null;
  executedAt?: string | null;
  createdAt: string;
}

export const expoStockApi = {
  getEntries: async (params?: { includeZeroBalance?: boolean }): Promise<ExpoStockEntryDto[]> => {
    const query = new URLSearchParams();
    if (params?.includeZeroBalance) query.set("includeZeroBalance", "true");
    const suffix = query.toString() ? `?${query.toString()}` : "";
    const { data } = await apiClient.get<ApiResponse<ExpoStockEntryDto[]>>(`/expo-stock/entries${suffix}`);
    return data.data ?? [];
  },

  getMovements: async (params?: { expoStockEntryId?: string; pendingOnly?: boolean }): Promise<ExpoStockMovementRequestDto[]> => {
    const query = new URLSearchParams();
    if (params?.expoStockEntryId) query.set("expoStockEntryId", params.expoStockEntryId);
    if (params?.pendingOnly) query.set("pendingOnly", "true");
    const suffix = query.toString() ? `?${query.toString()}` : "";
    const { data } = await apiClient.get<ApiResponse<ExpoStockMovementRequestDto[]>>(`/expo-stock/movements${suffix}`);
    return data.data ?? [];
  },

  requestMove: async (payload: {
    greenhouseStockEntryId: string;
    quantity: number;
    includeFillingReserve: boolean;
    reason: string;
  }): Promise<ExpoStockMovementRequestDto> => {
    const { data } = await apiClient.post<ApiResponse<ExpoStockMovementRequestDto>>("/expo-stock/move", payload);
    return data.data;
  },

  requestReturn: async (payload: {
    expoStockEntryId: string;
    quantity: number;
    reason: string;
  }): Promise<ExpoStockMovementRequestDto> => {
    const { data } = await apiClient.post<ApiResponse<ExpoStockMovementRequestDto>>("/expo-stock/return", payload);
    return data.data;
  },

  approveMovement: async (id: string, decisionComment?: string): Promise<ExpoStockMovementRequestDto> => {
    const { data } = await apiClient.post<ApiResponse<ExpoStockMovementRequestDto>>(
      `/expo-stock/movements/${id}/approve`,
      { decisionComment: decisionComment ?? null }
    );
    return data.data;
  },

  rejectMovement: async (id: string, decisionComment: string): Promise<ExpoStockMovementRequestDto> => {
    const { data } = await apiClient.post<ApiResponse<ExpoStockMovementRequestDto>>(
      `/expo-stock/movements/${id}/reject`,
      { decisionComment }
    );
    return data.data;
  },
};
